import { latestReady, type RecapDoc } from '../../shared/recap';
import type { PinEvent } from '../../shared/events';
import {
  addDoc,
  arrayUnion,
  collection,
  collectionGroup,
  deleteField,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  COL,
  DEFAULT_LIMITS,
  canSee,
  dayKey,
  inQuietAudience,
  shouldBeQuiet,
  previousDay,
  mediaUrl,
  streakDayFor,
  shownMedia,
} from '../../shared/constants';
import type {
  DailyDoc,
  ErrorDoc,
  DailyPostDoc,
  DailyReplyDoc,
  Limits,
  MediaRef,
  MessageDoc,
  PollOption,
  PollVoteDoc,
  SnapDecor,
  PinDoc,
  TypingDoc,
  UploadTarget,
  UserDoc,
} from '../../shared/types';
import { storedDecor } from '../../shared/snapDecor';
import { MEDIA_GATEWAY, auth, db, functions } from './firebase';
import { tell } from './errors';
import type { TypingChange } from './typing';

export interface WithId<T> {
  id: string;
  data: T;
}

/**
 * A daily post that remembers which day it is filed under.
 *
 * The screen watches two days at once, so "which day" stops being something
 * the caller can assume and has to travel with the post: the roster counts
 * only the day being answered, while the pictures show both.
 */
export type DayPost = WithId<DailyPostDoc> & { day: string };

/**
 * Quiet messages are test traffic, shown only to the quiet audience.
 *
 * The paged readers -- watchMessages, fetchOlderMessages, fetchMediaPage --
 * return every row and leave this to hooks.ts, because their callers read a
 * short page as the end of history: filtered here, one hidden test message
 * would stop the family scrolling back. The one-shot readers apply it here.
 */
/** Who is looking, for the hooks that hide the test account from the family. */
export const viewerEmail = (): string | null | undefined => auth.currentUser?.email;

export function visibleRows<T extends { data: { quiet?: boolean } }>(rows: T[]): T[] {
  const email = auth.currentUser?.email;
  return rows.filter((row) => canSee(row.data, email));
}

function visible(docs: { id: string; data: () => unknown }[]): WithId<MessageDoc>[] {
  return visibleRows(docs.map((d) => ({ id: d.id, data: d.data() as MessageDoc })));
}

const rowsOf = (docs: { id: string; data: () => unknown }[]): WithId<MessageDoc>[] =>
  docs.map((d) => ({ id: d.id, data: d.data() as MessageDoc }));

/**
 * The chat stream: recent messages plus anything kept. Ordered by the
 * server's clock, not the sender's.
 */
export function watchMessages(
  onChange: (messages: WithId<MessageDoc>[]) => void,
  pageSize = 100,
): Unsubscribe {
  // Only the newest page stays subscribed. This used to widen its limit to
  // scroll back, which was fine when chat expired daily and the whole thread
  // was small; with the GroupMe archive in here that would put a live listener
  // over thousands of documents to read messages from 2017 that cannot change.
  // Older pages are fetched once, by cursor, in fetchOlderMessages.
  const q = query(
    collection(db, COL.messages),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  );
  return onSnapshot(
    q,
    (snap) => {
      onChange(rowsOf(snap.docs).reverse());
    },
    failed('messages'),
  );
}

/**
 * A listener the server refused or lost: said, so it is not silent. Each one
 * that keeps a screen waiting also settles it to empty, where it fails --
 * otherwise a refused read was a spinner forever, with nothing to show why.
 */
function failed(what: string) {
  return (error: unknown) => {
    const uid = auth.currentUser?.uid;
    if (uid) void tell(uid, error, 'reported', `listener: ${what}`);
  };
}

/** A Firestore Timestamp, which is all the cursor and the sort need. */
type SortKey = { toMillis: () => number };

const sortKeyOf = (message: WithId<MessageDoc>): SortKey | null =>
  (message.data.createdAt as SortKey | null) ?? null;

/**
 * The page of messages immediately older than `before`. A plain read, not a
 * subscription: history that far back does not change, and holding listeners
 * open over it costs memory for nothing.
 */
export async function fetchOlderMessages(
  before: WithId<MessageDoc>,
  pageSize = 100,
): Promise<WithId<MessageDoc>[]> {
  const cursor = sortKeyOf(before);
  if (!cursor) return [];
  const snap = await getDocs(
    query(
      collection(db, COL.messages),
      orderBy('createdAt', 'desc'),
      startAfter(cursor),
      limit(pageSize),
    ),
  );
  return rowsOf(snap.docs).reverse();
}

/**
 * The messages either side of one particular message, so a search result can
 * be read where it happened. Two bounded reads beat paging back from today:
 * reaching 2017 that way would mean forty round trips and every message in
 * between held in memory.
 */
export async function fetchAround(
  id: string,
  span = 40,
): Promise<{ messages: WithId<MessageDoc>[]; found: boolean }> {
  const target = await getDoc(doc(db, COL.messages, id));
  if (!target.exists() || !canSee(target.data() as MessageDoc, auth.currentUser?.email)) {
    return { messages: [], found: false };
  }

  const anchor = (target.data() as MessageDoc).createdAt as SortKey;
  const half = Math.floor(span / 2);

  const [before, after] = await Promise.all([
    getDocs(
      query(
        collection(db, COL.messages),
        orderBy('createdAt', 'desc'),
        startAfter(anchor),
        limit(half),
      ),
    ),
    getDocs(
      query(
        collection(db, COL.messages),
        orderBy('createdAt', 'asc'),
        startAfter(anchor),
        limit(half),
      ),
    ),
  ]);

  const rows = [
    ...visible(before.docs).reverse(),
    { id: target.id, data: target.data() as MessageDoc },
    ...visible(after.docs),
  ];
  return { messages: rows, found: true };
}

/**
 * Every message, for searching. Four thousand documents is a few megabytes,
 * and Firestore's persistent cache keeps them, so this is slow once per device
 * rather than once per search.
 */
export async function fetchAllMessages(): Promise<WithId<MessageDoc>[]> {
  const snap = await getDocs(
    query(collection(db, COL.messages), orderBy('createdAt', 'desc')),
  );
  return visible(snap.docs);
}

/**
 * A page of the messages that carry media, newest first. Filtered on hasMedia
 * because Firestore cannot ask whether an array is non-empty.
 */
export async function fetchMediaPage(
  after: WithId<MessageDoc> | null,
  pageSize = 60,
): Promise<WithId<MessageDoc>[]> {
  const cursor = after ? sortKeyOf(after) : null;
  const snap = await getDocs(
    query(
      collection(db, COL.messages),
      where('hasMedia', '==', true),
      orderBy('createdAt', 'desc'),
      ...(cursor ? [startAfter(cursor)] : []),
      limit(pageSize),
    ),
  );
  return rowsOf(snap.docs);
}

/**
 * Every message carrying media, for Memories. The whole archive rather than a
 * page: picking one at random means having them all to pick from, and a cursor
 * only walks one way. Firestore's cache makes this slow once per device.
 */
export async function fetchAllMedia(): Promise<WithId<MessageDoc>[]> {
  const snap = await getDocs(
    query(
      collection(db, COL.messages),
      where('hasMedia', '==', true),
      orderBy('createdAt', 'desc'),
    ),
  );
  return visible(snap.docs);
}

/**
 * Today's dailies, which live in their own collection and never in chat.
 *
 * Two days, not one. The prompt doc is always today's, because that is where
 * firing is recorded — but the GiviSnaps on the screen are the ones answering
 * the prompt that is live, and until today's fires that is still last night's.
 *
 * Without the split, everybody's GiviSnaps vanished at midnight: the screen
 * asked for the calendar day, got an empty collection, and showed nothing for
 * however many hours were left before the next prompt went out. Which also
 * contradicted the streak, still counting that same night as unfinished.
 */
export function watchToday(
  onChange: (
    day: DailyDoc | null,
    posts: DayPost[],
    showing: string,
    /** Whether the posts have actually been read, rather than not yet. */
    settled: boolean,
  ) => void,
): Unsubscribe {
  const day = dayKey(new Date());
  const before = previousDay(day);
  const dayRef = doc(db, COL.dailies, day);

  let dayDoc: DailyDoc | null = null;
  let showing = streakDayFor(day, false);

  /*
   * Three days, always, rather than one that moves.
   *
   * It used to follow a single day and switch which one when the prompt
   * fired — and everything answering the prompt before it vanished from the
   * screen at that instant. Dad answered last night's seventeen minutes
   * before today's went out, and the moment it did his GiviSnap was filed
   * under a day nothing was watching: the row in chat still said he had
   * posted, and there was nowhere to go and see it.
   *
   * Two was not enough either, for the same reason one step further out. A
   * post made before the day's prompt fires is filed under the day before, so
   * Ethan's yesterday afternoon went in under the day before that — and at
   * midnight it fell off the back of a two-day window while still being well
   * inside its two days of life. It existed, it had not expired, and nothing
   * was looking at it.
   *
   * Three listeners rather than a clever one. Retention is two days and a post
   * can be filed one day behind the day it was taken, so three day-keys is
   * every post that can exist, and nothing can fall between them.
   */
  /** Oldest first, so the merge below reads in the order things happened. */
  const days = [previousDay(before), before, day];

  const held = new Map<string, DayPost[]>();
  const read = new Set<string>();

  const publish = () => {
    const merged = days
      .flatMap((which) => held.get(which) ?? [])
      .sort((a, b) => at(a.data.createdAt) - at(b.data.createdAt));
    onChange(dayDoc, merged, showing, read.size === days.length);
  };

  const follow = (which: string) =>
    onSnapshot(
      query(
        collection(doc(db, COL.dailies, which), COL.dailyPosts),
        orderBy('createdAt', 'asc'),
      ),
      (snap) => {
        held.set(
          which,
          snap.docs.map((d) => ({
            id: d.id,
            day: which,
            data: d.data() as DailyPostDoc,
          })),
        );
        read.add(which);
        publish();
      },
      (error) => {
        failed('day posts')(error);
        // Nothing to show from this day, but it is read: the screen settles.
        held.set(which, []);
        read.add(which);
        publish();
      },
    );

  const stopDays = days.map(follow);

  const stopDoc = onSnapshot(
    dayRef,
    (snap) => {
      dayDoc = (snap.data() as DailyDoc | undefined) ?? null;
      // Still the day a new one is written to and the streak is counted
      // against; it is only the watching that no longer moves with it.
      showing = streakDayFor(day, Boolean(dayDoc?.firedAt));
      publish();
    },
    failed('day'),
  );

  return () => {
    stopDoc();
    for (const stop of stopDays) stop();
  };
}

function at(value: unknown): number {
  return (value as { toMillis?: () => number } | null)?.toMillis?.() ?? 0;
}

/**
 * Text is the one thing a client writes to chat directly; rules allow it and
 * block every other message kind, so media cannot be forged.
 */
/**
 * Where somebody is, as two numbers. No callable and no bytes: there is
 * nothing to authorize and nothing to store but the pair, which the rules
 * check the range of themselves.
 */
/**
 * Whether what this person sends now is quiet. Only the quiet audience can
 * have switched quiet sending on, so only they pay for reading their own doc.
 */
async function sendsQuietly(
  email: string | null | undefined,
  answering: { quiet?: boolean } | null | undefined,
): Promise<boolean> {
  const uid = auth.currentUser?.uid;
  const quietSending =
    !!uid &&
    inQuietAudience(email) &&
    (await getDoc(doc(db, COL.users, uid))).get('quietSending') === true;
  return shouldBeQuiet(email, answering, quietSending);
}

export async function sendLocation(lat: number, lng: number): Promise<void> {
  const quiet = await sendsQuietly(auth.currentUser?.email, null);
  await addDoc(collection(db, COL.messages), {
    ...(quiet ? { quiet: true } : {}),
    authorUid: auth.currentUser?.uid,
    createdAt: serverTimestamp(),
    kind: 'location',
    // Rounded to about a metre. More digits than that is false precision from
    // a phone and a longer number for a rule to check.
    place: { lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)) },
    likes: {},
    expireAt: null,
  });
}

export async function sendText(
  authorUid: string,
  text: string,
  mentions: string[] = [],
  replyTo?: string,
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;
  const email = auth.currentUser?.email;
  // Only the quiet audience pays for reading what it answers.
  const answering =
    replyTo && inQuietAudience(email)
      ? ((await getDoc(doc(db, COL.messages, replyTo))).data() as MessageDoc | undefined)
      : null;
  const quiet = await sendsQuietly(email, answering);
  await addDoc(collection(db, COL.messages), {
    ...(quiet ? { quiet: true } : {}),
    authorUid,
    createdAt: serverTimestamp(),
    kind: 'text',
    text: trimmed.slice(0, 2000),
    likes: {},
    ...(mentions.length ? { mentions } : {}),
    ...(replyTo ? { replyTo } : {}),
    // Chat is the family's archive: null means kept forever. Only dailies
    // expire now, so config/limits.retentionDays no longer reaches this path.
    expireAt: null,
  });
}

/**
 * Rewrite your own message. The stamp is what lets the thread show that it
 * changed; rules require it, so an edit cannot be made to look original.
 */
export function editMessage(messageId: string, text: string): Promise<void> {
  return updateDoc(doc(db, COL.messages, messageId), {
    text,
    editedAt: serverTimestamp(),
  });
}

/**
 * Your emoji on a chat message. Rules restrict the update to your own key and
 * check which key changed rather than what it holds, so widening this from a
 * boolean to an emoji to a list of them has needed no rules change.
 *
 * The whole list is written rather than an arrayUnion, which is safe because
 * nobody else can write your key: there is no second writer to race with.
 */
export function setMessageReaction(
  messageId: string,
  uid: string,
  emojis: string[],
): Promise<void> {
  return updateDoc(doc(db, COL.messages, messageId), {
    [`likes.${uid}`]: emojis.length > 0 ? emojis : deleteField(),
  });
}

export const savePost = httpsCallable<{ target: UploadTarget; authorUid?: string }, { saved: true }>(
  functions,
  'savePost',
);
export const unsavePost = httpsCallable<
  { target: UploadTarget; authorUid?: string },
  { deleted: boolean }
>(functions, 'unsavePost');
export const deletePost = httpsCallable<
  { target: UploadTarget; authorUid?: string },
  { deleted: true }
>(functions, 'deletePost');
/** Test messages, gone outright. The server refuses anyone outside the quiet audience. */
export const clearQuiet = httpsCallable<void, { deleted: number }>(functions, 'clearQuiet');
export const announceDaily = httpsCallable<
  { day: string; postId?: string },
  { announced: true }
>(
  functions,
  'announceDaily',
);

export const createPoll = httpsCallable<
  { question: string; labels: string[]; multi?: boolean },
  { messageId: string; options: PollOption[] }
>(functions, 'createPoll');
export const votePoll = httpsCallable<
  { messageId: string; optionIds: string[] },
  { voted: boolean }
>(functions, 'votePoll');
export const editPoll = httpsCallable<
  { messageId: string; question?: string; options?: Array<{ id?: string; label: string }> },
  { edited: true }
>(functions, 'editPoll');
export const closePoll = httpsCallable<{ messageId: string }, { closed: true }>(
  functions,
  'closePoll',
);

/**
 * Who voted for what, when this person is allowed to know.
 *
 * The rules refuse this outright until you have voted, so a refusal is an
 * ordinary state rather than a fault: it means the poll is still secret, and
 * the screen shows the total instead. Reported as `false` rather than thrown,
 * because a listener that rejects is otherwise an unhandled rejection on every
 * poll in the thread you have not answered.
 */
export function watchPollVotes(
  messageId: string,
  onChange: (votes: Record<string, PollVoteDoc>, allowed: boolean) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, COL.messages, messageId, 'votes'),
    (snap) => {
      const votes: Record<string, PollVoteDoc> = {};
      for (const each of snap.docs) votes[each.id] = each.data() as PollVoteDoc;
      onChange(votes, true);
    },
    () => onChange({}, false),
  );
}

/** Media refs store keys, not URLs; the gateway origin is applied here. */
/**
 * Whose face to draw: their own picture if they have uploaded one, and
 * whatever Google gave us otherwise.
 *
 * Here rather than inside Avatar because the key has to meet the gateway
 * somewhere, and this is where every other key does.
 */
export function avatarOf(user?: UserDoc | null): string | null {
  if (user?.photo) return urlFor(shownMedia(user.photo));
  return user?.photoURL ?? null;
}

export function urlFor(media: MediaRef): string {
  return mediaUrl(MEDIA_GATEWAY, media.key);
}

/** The same, for the few things that store a key without the rest of a MediaRef. */
export function urlForKey(key: string): string {
  return mediaUrl(MEDIA_GATEWAY, key);
}


/**
 * The faults the family's phones have written down, newest first.
 *
 * Only an admin can read these, so this is only ever called from the admin
 * screen. Capped because the point is the last few rather than the archive --
 * a fault older than the screenful below it has either been fixed or is not
 * being fixed.
 */
export function watchErrors(
  onChange: (errors: WithId<ErrorDoc>[]) => void,
  most = 50,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, COL.errors), orderBy('at', 'desc'), limit(most)),
    (snap) => {
      onChange(snap.docs.map((d) => ({ id: d.id, data: d.data() as ErrorDoc })));
    },
    // An admin who is not an admin any more, or rules mid-deploy. Nothing to
    // show is the right answer and a thrown listener is not.
    () => onChange([]),
  );
}

/** Clears one once it has been dealt with. */
export async function forgetError(id: string): Promise<void> {
  await deleteDoc(doc(db, COL.errors, id));
}

/**
 * Who has something in the composer, as changes rather than a list: a reader
 * times each person from when their change reached it (see typing.ts), so it
 * needs to know what changed, not just what is there.
 */
export function watchTyping(onChange: (changes: TypingChange[]) => void): Unsubscribe {
  return onSnapshot(
    collection(db, COL.typing),
    (snap) => {
      onChange(
        snap.docChanges().map((change) => {
          const data = change.doc.data() as TypingDoc;
          const at = data.at as { toMillis?: () => number } | null;
          return {
            uid: change.doc.id,
            type: change.type,
            data,
            atMs: at?.toMillis?.() ?? null,
          };
        }),
      );
    },
    failed('typing'),
  );
}

/** The pin over the chat, or null when nothing is pinned. */
/**
 * The week's recap that is out, if one is: the newest marked ready. There are
 * never more than two -- the week out, and the next one filling up -- since
 * each Sunday takes the old one down (functions/src/recap.ts).
 */
export function watchRecap(onChange: (recap: RecapDoc | null) => void): Unsubscribe {
  return onSnapshot(
    collection(db, COL.recaps),
    (snap) => onChange(latestReady(snap.docs.map((d) => d.data() as RecapDoc))),
    () => onChange(null),
  );
}

/** A pin, with the id of its own document: the message's id, or 'chat' for the one from before there could be several. */
export type PinEntry = PinDoc & { id: string };

/**
 * Every pin over the chat. There used to be one, at pins/chat; now each pinned
 * message has its own, under its id, and the old one is read as one more.
 */
export function watchPins(onChange: (pins: PinEntry[]) => void): Unsubscribe {
  return onSnapshot(
    collection(db, COL.pins),
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as PinDoc), id: d.id }))),
    // Pins that cannot be read are no pins to show.
    () => onChange([]),
  );
}

/**
 * One message by id, kept up to date: the pinned one, which may be far older
 * than the page of the thread that is subscribed.
 */
export function watchMessage(
  id: string,
  onChange: (message: WithId<MessageDoc> | null) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, COL.messages, id),
    (snap) => onChange(snap.exists() ? { id: snap.id, data: snap.data() as MessageDoc } : null),
    () => onChange(null),
  );
}

/** Pins this message over the chat for everyone, replacing whatever was pinned. */
/**
 * Adds a pin, as an event when given one. Pinning the same message again
 * replaces its pin, which is how an event's day or name is changed.
 */
export async function pinMessage(messageId: string, uid: string, event?: PinEvent): Promise<void> {
  await setDoc(doc(db, COL.pins, messageId), {
    messageId,
    by: uid,
    at: serverTimestamp(),
    ...(event ? { event } : {}),
  });
}

/** Takes one pin down, by its document id. */
export async function unpinMessage(pinId: string): Promise<void> {
  await deleteDoc(doc(db, COL.pins, pinId));
}

/** Stamps you as typing. A whole write, so a stamp stops being quiet once you do. */
export async function setTyping(uid: string, quiet: boolean): Promise<void> {
  await setDoc(doc(db, COL.typing, uid), {
    at: serverTimestamp(),
    ...(quiet ? { quiet: true } : {}),
  });
}

export async function clearTyping(uid: string): Promise<void> {
  await deleteDoc(doc(db, COL.typing, uid));
}

/** Display names and avatars, keyed by uid. Small collection; read whole. */
export function watchUsers(
  onChange: (users: Record<string, UserDoc>) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, COL.users),
    (snap) => {
      const users: Record<string, UserDoc> = {};
      for (const d of snap.docs) users[d.id] = d.data() as UserDoc;
      onChange(users);
    },
    failed('users'),
  );
}

/**
 * config/limits drives client-side pre-validation and the copy in the UI.
 * The server checks all of it again; this only saves a round trip.
 */
export function watchLimits(onChange: (limits: Limits) => void): Unsubscribe {
  return onSnapshot(
    doc(db, COL.configLimits),
    (snap) => {
      onChange({ ...DEFAULT_LIMITS, ...(snap.data() as Partial<Limits> | undefined) });
    },
    // The defaults stand in, which is what an unread config already means.
    failed('limits'),
  );
}

/**
 * The words and emojis on your own GiviSnap, written once its photos have
 * landed.
 *
 * They cannot ride along with the upload: finalizeUpload creates the post
 * with admin credentials and knows nothing about them. Rules let the author
 * set them, so the client writes them the moment the document exists.
 */
export function setDailyDecor(day: string, postId: string, decor: SnapDecor): Promise<void> {
  return updateDoc(doc(db, COL.dailies, day, COL.dailyPosts, postId), { ...storedDecor(decor) });
}

/** Your emoji on a daily. Rules restrict the write to your key. */
export function setDailyReaction(
  day: string,
  postId: string,
  uid: string,
  emojis: string[],
): Promise<void> {
  return updateDoc(doc(db, COL.dailies, day, COL.dailyPosts, postId), {
    [`reactions.${uid}`]: emojis.length > 0 ? emojis : deleteField(),
  });
}

/**
 * Your one look at a snap, spent. Additive for the same reason as the daily's
 * below: several of the family can be holding the same snap open at once.
 */
export function markSnapViewed(messageId: string, uid: string): Promise<void> {
  return updateDoc(doc(db, COL.messages, messageId), { viewedBy: arrayUnion(uid) });
}

/** "Seen by" on a daily. Additive, so two viewers cannot clobber each other. */
export function markDailySeen(
  day: string,
  postId: string,
  uid: string,
): Promise<void> {
  return updateDoc(doc(db, COL.dailies, day, COL.dailyPosts, postId), {
    seenBy: arrayUnion(uid),
  });
}

/**
 * The replies under one daily, live. They expire with the photo, so this never
 * grows the way chat does and needs no paging.
 */
export function watchDailyReplies(
  day: string,
  postId: string,
  onChange: (replies: WithId<DailyReplyDoc>[]) => void,
): Unsubscribe {
  return onSnapshot(
    query(
      collection(db, COL.dailies, day, COL.dailyPosts, postId, COL.dailyReplies),
      orderBy('createdAt', 'asc'),
    ),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, data: d.data() as DailyReplyDoc }))),
    (error) => {
      failed('daily replies')(error);
      onChange([]);
    },
  );
}

/** Say something about one daily. Dies with the photo it is about. */
export async function sendDailyReply(
  day: string,
  postId: string,
  authorUid: string,
  text: string,
  mentions: string[] = [],
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return;
  await addDoc(
    collection(db, COL.dailies, day, COL.dailyPosts, postId, COL.dailyReplies),
    {
      authorUid,
      createdAt: serverTimestamp(),
      text: trimmed.slice(0, 2000),
      // Capped for the same reason chat's are: @FamBam expands to the whole
      // family and the cap is what stops a doctored client naming more.
      ...(mentions.length ? { mentions: mentions.slice(0, 20) } : {}),
      // Matches the daily's own window rather than chat's permanence.
      expireAt: new Date(Date.now() + 86400_000),
    },
  );
}

/** Chat's pointer rows carry a dailyId rather than media of their own. */
export function isPointer(message: MessageDoc): boolean {
  return message.kind === 'dailyPointer';
}


/**
 * One specific day's dailies, for opening an older post from a chat pointer.
 * watchToday only ever looks at today, so without this the pager had nothing
 * to show for any earlier date.
 */
export function watchDay(
  day: string,
  onChange: (
    daily: DailyDoc | null,
    posts: WithId<DailyPostDoc>[],
    /** Whether the posts have actually been read, rather than not yet. */
    settled: boolean,
  ) => void,
): Unsubscribe {
  const dayRef = doc(db, COL.dailies, day);

  let dayDoc: DailyDoc | null = null;
  let posts: WithId<DailyPostDoc>[] = [];
  // An empty day and a day that has not loaded are the same array, and the
  // screen has to tell them apart: one is a spinner, the other is the news
  // that the thing being asked for has expired.
  let settled = false;

  const stopDay = onSnapshot(
    dayRef,
    (snap) => {
      dayDoc = (snap.data() as DailyDoc | undefined) ?? null;
      onChange(dayDoc, posts, settled);
    },
    failed('archive day'),
  );

  const stopPosts = onSnapshot(
    query(collection(dayRef, COL.dailyPosts), orderBy('createdAt', 'asc')),
    (snap) => {
      posts = snap.docs.map((d) => ({ id: d.id, data: d.data() as DailyPostDoc }));
      settled = true;
      onChange(dayDoc, posts, settled);
    },
    // Settled with nothing: the screen says the day has gone, not spins.
    (error) => {
      failed('archive day posts')(error);
      settled = true;
      onChange(dayDoc, posts, settled);
    },
  );

  return () => {
    stopDay();
    stopPosts();
  };
}

export interface SavedDaily {
  day: string;
  /** The post's own id, which is what the pager and the route are keyed by. */
  id: string;
  data: DailyPostDoc;
}

/**
 * Kept dailies, across every day. A collection group query, because the posts
 * live under dailies/{date}/posts rather than in one collection — which is
 * also why firestore.rules needs a rule at the collection group path.
 */
export function watchSavedDailies(
  onChange: (dailies: SavedDaily[]) => void,
): Unsubscribe {
  const q = query(
    collectionGroup(db, COL.dailyPosts),
    where('expireAt', '==', null),
    orderBy('createdAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) =>
      onChange(
        snap.docs.map((d) => ({
          day: d.ref.parent.parent?.id ?? '',
          // The doc id, not the author. It was read as a uid back when a person
          // had one post a day and the two were the same string; they have not
          // been since, and every link built from it pointed at nothing.
          id: d.id,
          data: d.data() as DailyPostDoc,
        })),
      ),
    // A collection group query: a missing index fails it outright.
    (error) => {
      failed('kept dailies')(error);
      onChange([]);
    },
  );
}

/**
 * Hands a photo or video to the OS share sheet as a real file, which is the
 * only way it lands in Photos on iOS — a plain download link opens it in a
 * tab there instead. Falls back to a download link everywhere else.
 */
/** iPad reports itself as a Mac, so touch points are what give it away. */
function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export async function saveToPhone(media: MediaRef, name?: string): Promise<void> {
  const url = urlFor(media);
  const filename =
    name ?? `givisnap-${media.key.split('/').pop() ?? 'media'}`;

  /*
   * The gateway authorises by cookie, so the fetch has to carry it — and it
   * has to skip the HTTP cache to get it.
   *
   * These responses are stored `immutable` for a year, and every one already
   * in the cache was put there by an <img>, which needs no CORS headers and so
   * was served none. The browser will happily reuse one of those for a cors
   * fetch and then fail the check against a response that never claimed to
   * allow it. `reload` goes past the cache and replaces the entry with one
   * that carries the headers.
   */
  const response = await fetch(url, {
    credentials: 'include',
    mode: 'cors',
    cache: 'reload',
  });
  if (!response.ok) throw new Error(`could not fetch media: ${response.status}`);
  const blob = await response.blob();

  /*
   * A download, not a share sheet.
   *
   * Everywhere but iOS, a download link puts the file straight in Downloads
   * with no further asking, which is what "save" means. The share sheet was
   * the first choice and it is one more decision than the button implies.
   *
   * iOS is the exception and has to stay one: a download link there opens the
   * picture in a tab rather than saving it, and the share sheet is the only
   * route into Photos. Sniffing for it is unpleasant, but there is nothing to
   * feature-detect — the link does not fail on iOS, it just does the wrong
   * thing quietly.
   */
  if (isIOS()) {
    const file = new File([blob], filename, { type: blob.type });
    if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return;
      } catch (err) {
        // A cancelled share sheet is not a failure worth surfacing.
        if ((err as Error).name === 'AbortError') return;
        // Anything else: fall through to the download link.
      }
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke late; Safari needs the URL alive past the click.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
